import type { FormTableColumn } from '@connected/form-fields/components/form-table';

import { NumberField } from '@connected/form-fields/components/form-table';

import type {
    DeviceForm,
    DeviceFormValues,
    DevicePatcher,
} from '../../distribution-board-device/types/hook-types';

// ============================================================================
// Types
// ============================================================================

export type ImpedanceColumnConfig = {
    form: DeviceForm;
    createPatcher: (deviceId: string) => DevicePatcher;
};

// ============================================================================
// Column Factory
// ============================================================================

/**
 * Create columns for the impedance measurements page.
 * This page shows Zi/Zs fields with read-only identifier.
 */
export function createImpedanceColumns(config: ImpedanceColumnConfig): FormTableColumn<DeviceFormValues>[] {
    const { createPatcher } = config;

    const columns: FormTableColumn<DeviceFormValues>[] = [
        // Identifier (Naam) - Read-only
        {
            accessorKey: 'identifier',
            header: 'Naam',
            size: '120px',
            minSize: '120px',
            sticky: true,
            cell: ({ form, fieldName }) => (
                <form.Field name={fieldName}>
                    {field => (
                        <span className="px-2 py-1 text-sm text-foreground">
                            {field.state.value as string}
                        </span>
                    )}
                </form.Field>
            ),
        },

        // Zi fields
        {
            accessorKey: 'ziResistance',
            header: 'Zi (Ω)',
            size: '100px',
            minSize: '100px',
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('ziResistance'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
        {
            accessorKey: 'ziCurrent',
            header: 'Ik (Zi)',
            size: '100px',
            minSize: '100px',
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('ziCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
        {
            accessorKey: 'ziMinimalCurrent',
            header: 'Acceptatiecriteria Zi',
            size: '175px',
            minSize: '175px',
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('ziMinimalCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },

        // Zs fields
        {
            accessorKey: 'zsResistance',
            header: 'Zs (Ω)',
            size: '100px',
            minSize: '100px',
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('zsResistance'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
        {
            accessorKey: 'zsCurrent',
            header: 'Ik (Zs)',
            size: '100px',
            minSize: '100px',
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('zsCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
        {
            accessorKey: 'zsMinimalCurrent',
            header: 'Acceptatiecriteria Zs',
            size: '175px',
            minSize: '175px',
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('zsMinimalCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
    ];

    return columns;
}
